import React, { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import Login from "./modules/login/Login";
import Home from "./modules/home/Home";
import Enquire from "./modules/enquire/Enquire";
import Receive from "./modules/receive/Receive";
import StockCheck from "./modules/stockcheck/StockCheck";
import StockExtract from "./modules/stockcheck/StockExtract";
import "./modules/Modules.css";
import PurchaseOrder from "./modules/receive/PurchaseOrder/PurchaseOrder";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store/store";
import { clearAuth } from "./store/slices/authSlice";
import type { PersistState } from "redux-persist";
import { LOGOUT_BROADCAST_KEY } from "./utilities/Tools";

const PrivateRoute = () => {
  const auth = useSelector((state: RootState) => state.root.auth);
  const rehydrated =
    (auth as typeof auth & { _persist: PersistState })._persist?.rehydrated ??
    false;

  if (!rehydrated) return null; // wait for sessionStorage auth rehydration
  return auth.token ? <Outlet /> : <Navigate to="/login" replace />;
};

// Listens for logouts from other tabs and navigates this tab to /login
const CrossTabLogoutListener = () => {
  const userId = useSelector((state: RootState) => state.root.auth.id);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOGOUT_BROADCAST_KEY && e.newValue === userId) {
        dispatch(clearAuth());
        navigate("/login", { replace: true });
      }
      localStorage.removeItem(LOGOUT_BROADCAST_KEY);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [dispatch, navigate, userId]);

  return null;
};

const App = () => {
  //global animation canceller on resize
  let resizeTimer: ReturnType<typeof setTimeout>;
  window.addEventListener("resize", () => {
    document.body.classList.add("resize-animation-stopper");
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.body.classList.remove("resize-animation-stopper");
    }, 400);
  });

  return (
    <div className="App">
      <BrowserRouter>
        <CrossTabLogoutListener />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<Home />} />
            <Route path="/enquire" element={<Enquire />} />
            {import.meta.env.VITE_BUILD_ENV?.toUpperCase() != "PLA" ? (
              <>
                <Route path="/receive" element={<Receive />} />
                <Route
                  path="/receive/purchaseorder"
                  element={<PurchaseOrder />}
                />
                <Route path="/stockcheck" element={<StockCheck />} />
                <Route
                  path="/stockcheck/stockextract"
                  element={<StockExtract />}
                />
              </>
            ) : null}
            <Route path="*" element={<Home />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
};

export default App;
